import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ChurchDetailDialog } from '@/components/church-detail-dialog';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { StatCard } from '@/components/shared/stat-card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { api, type OffsetPage } from '@/lib/api';
import { formatDate } from '@/utils/format';

interface PlatformChurchRow {
  church: {
    id: string;
    name: string;
    campusName: string;
    parentChurchId: string | null;
    status: 'active' | 'suspended' | 'banned' | 'deactivated';
    createdAt: string;
  };
  subscription: { plan: string; status: string; includedSeats: number; seatsInUse: number };
  payments: { suspended: boolean };
}

const MANUAL_PAID_PLANS = ['essential', 'professional', 'enterprise'] as const;

const churchPlanLabel = (row: PlatformChurchRow, t: TFunction): string => {
  const { plan, status } = row.subscription;
  const onTrial = status === 'trialing' || plan === 'trial';
  if (onTrial && (MANUAL_PAID_PLANS as readonly string[]).includes(plan)) {
    return t('churches.detail.trialingPlan', { plan: t(`billing.plans.${plan}`) });
  }
  return t(`billing.plans.${plan}`);
};

interface PlatformStats {
  churches: { total: number; active: number; suspended: number };
  subscriptions: { trialing: number; active: number; pastDue: number; expired: number };
  users: { total: number; active: number };
}

export const ChurchesPage = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [selectedChurchId, setSelectedChurchId] = useState<string | null>(null);
  const subscriptionStatus = searchParams.get('subscriptionStatus') ?? '';
  const paymentsSuspended = searchParams.get('paymentsSuspended') ?? '';

  useEffect(() => {
    const churchId = searchParams.get('churchId');
    if (churchId) setSelectedChurchId(churchId);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [subscriptionStatus, paymentsSuspended]);

  const stats = useQuery({
    queryKey: ['platform-stats'],
    queryFn: () => api.get<PlatformStats>('/platform/stats'),
  });

  const churches = useQuery({
    queryKey: ['platform-churches', page, pageSize, search, subscriptionStatus, paymentsSuspended],
    queryFn: () =>
      api.get<{ data: PlatformChurchRow[]; page: OffsetPage }>('/platform/churches', {
        query: {
          page,
          pageSize,
          sort: 'name',
          ...(search ? { search } : {}),
          ...(subscriptionStatus ? { subscriptionStatus } : {}),
          ...(paymentsSuspended ? { paymentsSuspended } : {}),
        },
      }),
  });

  const rows = churches.data?.data ?? [];
  const pageMeta = churches.data?.page;

  const columns = useMemo((): DataTableColumn<PlatformChurchRow>[] => {
    return [
      {
        id: 'createdAt',
        header: t('churches.columns.created'),
        cell: (row) => formatDate(row.church.createdAt),
      },
      {
        id: 'name',
        header: t('churches.columns.church'),
        cell: (row) => <span className="font-medium">{row.church.name}</span>,
      },
      {
        id: 'campusName',
        header: t('churches.columns.branch'),
        cell: (row) => row.church.campusName,
      },
      {
        id: 'plan',
        header: t('churches.columns.plan'),
        cell: (row) => churchPlanLabel(row, t),
        hideBelowMd: true,
      },
      {
        id: 'subscription',
        header: t('churches.columns.subscription'),
        cell: (row) => t(`billing.statuses.${row.subscription.status as 'active'}`),
        hideBelowMd: true,
      },
      {
        id: 'seats',
        header: t('churches.columns.seats'),
        cell: (row) =>
          t('churches.seatsUsed', {
            used: row.subscription.seatsInUse,
            total: row.subscription.includedSeats,
          }),
        hideBelowMd: true,
      },
      {
        id: 'status',
        header: t('churches.columns.status'),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={row.church.status === 'active' ? 'success' : 'neutral'}>
              {t(`status.${row.church.status}`)}
            </Badge>
            {row.payments?.suspended ? (
              <Badge variant="warning">{t('churches.paymentsPaused')}</Badge>
            ) : null}
          </div>
        ),
      },
    ];
  }, [t]);

  const insightSummary = stats.data
    ? [
        { label: t('dashboard.churches'), value: stats.data.churches.total },
        { label: t('dashboard.subscriptions'), value: stats.data.subscriptions.active },
        { label: t('dashboard.users'), value: stats.data.users.total },
      ]
    : [];

  return (
    <div>
      <PageHeader title={t('churches.title')} description={t('churches.description')} />

      <ReadOnlyNotice message={t('churches.readOnlyNotice')} />

      <CollapsibleInsights
        storageKey="trinity.admin.insights.open"
        summary={insightSummary}
        defaultOpen={false}
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            label={t('dashboard.churches')}
            value={stats.data?.churches.total}
            hint={t('dashboard.activeChurches', { count: stats.data?.churches.active ?? 0 })}
            loading={stats.isLoading}
          />
          <StatCard
            label={t('dashboard.subscriptions')}
            value={stats.data?.subscriptions.active}
            hint={t('dashboard.pastDue', { count: stats.data?.subscriptions.pastDue ?? 0 })}
            loading={stats.isLoading}
          />
          <StatCard
            label={t('dashboard.users')}
            value={stats.data?.users.total}
            hint={t('dashboard.activeUsers', { count: stats.data?.users.active ?? 0 })}
            loading={stats.isLoading}
          />
        </div>
      </CollapsibleInsights>

      <div className="mb-4 max-w-md">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder={t('churches.search')}
          aria-label={t('churches.search')}
        />
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.church.id}
        loading={churches.isLoading}
        onRowClick={(row) => setSelectedChurchId(row.church.id)}
        empty={<p className="py-8 text-center text-body-sm text-fg-secondary">{t('churches.empty')}</p>}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <div className="mt-4">
          <Pagination
            page={pageMeta.page}
            pageSize={pageMeta.pageSize}
            totalItems={pageMeta.totalItems}
            totalPages={pageMeta.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <ChurchDetailDialog
        churchId={selectedChurchId}
        open={selectedChurchId !== null}
        onOpenChange={(open) => !open && setSelectedChurchId(null)}
      />
    </div>
  );
};
